import React, { useEffect, useMemo, useState } from "react";
import { AlertCircle, GitCompare, Loader, X } from "lucide-react";
import { toast } from "react-toastify";
import { compareTemplates, getTemplates } from "../apis/template";

const LOOKBACKS = [7, 30, 60, 90];

function metaId(t) {
  return t?.template_id || t?.meta_template_id || null;
}

function formatBlockReason(value) {
  if (!value) return "—";
  return String(value).replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

const CompareTemplatesModal = ({ isOpen, onClose, phoneId, templates }) => {
  const [loaded, setLoaded] = useState([]);

  useEffect(() => {
    if (!isOpen || !phoneId) return;
    let cancelled = false;
    getTemplates({
      phoneId,
      statusFilters: ["APPROVED"],
      limit: 200,
      offset: 0,
    })
      .then((res) => {
        if (!cancelled) setLoaded(res.data?.metaTemplates || []);
      })
      .catch(() => {
        if (!cancelled) setLoaded(templates || []);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, phoneId, templates]);

  const options = useMemo(
    () =>
      (loaded.length ? loaded : templates || []).filter(
        (t) => metaId(t) && String(t.status || "").toLowerCase() === "approved"
      ),
    [loaded, templates]
  );

  const [leftId, setLeftId] = useState("");
  const [rightId, setRightId] = useState("");
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [compareError, setCompareError] = useState(null);

  if (!isOpen) return null;

  const nameByMetaId = Object.fromEntries(
    options.map((t) => [String(metaId(t)), t.name])
  );

  const handleCompare = async (e) => {
    e.preventDefault();
    if (!phoneId) {
      toast.error("Select a WhatsApp number first");
      return;
    }
    if (!leftId || !rightId || leftId === rightId) {
      toast.error("Pick two different templates");
      return;
    }
    setLoading(true);
    setResult(null);
    setCompareError(null);
    try {
      const res = await compareTemplates({
        phoneId,
        templateId: leftId,
        otherTemplateId: rightId,
        days,
      });
      if (res.data?.success) {
        setResult(res.data);
      } else {
        throw new Error(res.data?.error || "Compare failed");
      }
    } catch (err) {
      const data = err.response?.data || {};
      const aisensy = data.aisensy || {};
      const msg =
        aisensy.error_user_msg ||
        data.error ||
        data.details ||
        err.message ||
        "Failed to compare templates";
      const title = data.title || aisensy.error_user_title || null;
      setCompareError({ title, msg });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const metrics = Array.isArray(result?.data) ? result.data : [];
  const sends = metrics.find((m) => m.metric === "MESSAGE_SENDS")?.number_values || [];
  const blockOrder = metrics.find((m) => m.metric === "BLOCK_RATE")?.order_by_relative_metric || [];
  const blockReasons = metrics.find((m) => m.metric === "TOP_BLOCK_REASON")?.string_values || [];

  const ids = [leftId, rightId].filter(Boolean);

  return (
    <>
      <div className="ping-drawer-backdrop" onClick={onClose} />
      <aside className="ping-drawer tpl-compare-drawer" role="dialog" aria-modal="true">
        <div className="ping-drawer-head">
          <div>
            <h3>Compare templates</h3>
            <p>
              Blutec Ping compares sends, block rate, and the top block reason. Both templates
              must be on this number and sent at least 1,000 times in the selected window.
            </p>
          </div>
          <button type="button" onClick={onClose} className="ping-btn ping-btn-ghost ping-btn-sm" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleCompare} className="tpl-compare-form">
          <div className="ping-drawer-body">
            <label>
              Template A
              <select
                value={leftId}
                onChange={(e) => setLeftId(e.target.value)}
              >
                <option value="">Select template</option>
                {options.map((t) => (
                  <option key={t.id} value={String(metaId(t))} disabled={String(metaId(t)) === rightId}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Template B
              <select
                value={rightId}
                onChange={(e) => setRightId(e.target.value)}
              >
                <option value="">Select template</option>
                {options.map((t) => (
                  <option key={t.id} value={String(metaId(t))} disabled={String(metaId(t)) === leftId}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
            <div>
              <span className="tpl-compare-lab">Lookback</span>
              <div className="tpl-compare-days">
                {LOOKBACKS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDays(d)}
                    className={`ping-btn ping-btn-sm ${days === d ? "ping-btn-primary" : "ping-btn-ghost"}`}
                  >
                    {d} days
                  </button>
                ))}
              </div>
            </div>

            {compareError && (
              <div className="ping-banner amber">
                <AlertCircle size={16} />
                <div>
                  {compareError.title && <b>{compareError.title}</b>}
                  <p>{compareError.msg}</p>
                </div>
              </div>
            )}

            {result && (
              <div className="tpl-compare-result">
                <p className="tpl-compare-window">Last {result.days} days</p>
                <div className="tpl-compare-cards">
                  {ids.map((id) => {
                    const send = sends.find((s) => String(s.key) === String(id));
                    const reason = blockReasons.find((s) => String(s.key) === String(id));
                    const better = blockOrder[0] && String(blockOrder[0]) === String(id);
                    return (
                      <div key={id} className="tpl-compare-card">
                        <p className="tpl-compare-name">{nameByMetaId[String(id)] || id}</p>
                        <p className="tpl-compare-id">{id}</p>
                        <p>Sends: <b>{send?.value ?? "—"}</b></p>
                        <p>
                          Block rate:{" "}
                          <b className={better ? "ok" : ""}>
                            {blockOrder.length
                              ? better
                                ? "Lower (better)"
                                : "Higher"
                              : "—"}
                          </b>
                        </p>
                        <p>
                          Top block reason: <b>{formatBlockReason(reason?.value)}</b>
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="ping-drawer-foot">
            <button
              type="button"
              onClick={onClose}
              className="ping-btn ping-btn-ghost ping-btn-sm"
            >
              Close
            </button>
            <button
              type="submit"
              disabled={loading || !leftId || !rightId}
              className="ping-btn ping-btn-primary ping-btn-sm"
            >
              {loading ? <Loader size={16} className="animate-spin" /> : <GitCompare size={16} />}
              Compare
            </button>
          </div>
        </form>
      </aside>
    </>
  );
};

export default CompareTemplatesModal;
